import type moment from 'moment'
import { useCallback, useContext, useId, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import RadioGroup from 'LuxKit/Radio/RadioGroup'
import RadioInput from 'LuxKit/Radio/RadioInput'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { getCashRefundCancellationTimelineDetails } from 'components/Common/CancellationPolicies/Flash/cashRefundCancellationPolicyTimelineUtils'
import {
  formatFlexibleCancellationPolicyDate,
  getFlexibleCancellationTimelineDetails,
} from 'components/Common/CancellationPolicies/Flash/flexibleCancellationPolicyTimelineUtils'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import Group from 'components/utils/Group'
import { CANCELLATION_POLICIES } from 'constants/cancellationPolicies'
import ModalContext from 'contexts/ModalContext'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import type { FlashCancellationOptionRow } from 'lib/offer/flashCancellationOptions'

import FlashCancellationOptionPriceDifference from './FlashCancellationOptionPriceDifference'
import FlashCancellationOptionsModal from './FlashCancellationOptionsModal'
import useFlashCancellationOptionCopy from './useFlashCancellationOptionCopy'

const StyledRadioInput = styled(RadioInput)`
  display: inline-flex;
  align-items: center;
`

interface OptionRowProps {
  row: FlashCancellationOptionRow
  radioGroupName: string
  checkIn?: string | moment.Moment
  propertyTimezoneOffset?: number
  showReserveForZero: boolean
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

function FlashCancellationOptionRowInput(props: OptionRowProps) {
  const { row, radioGroupName, checkIn, propertyTimezoneOffset, showReserveForZero, onChange } = props
  const intlDateFormatter = useIntlDateFormatter()
  const titleColour = useRebrandSuccessDarkColour()
  const getCopy = useFlashCancellationOptionCopy()
  const copy = getCopy(row.policyType)
  const cancellationPolicy = useMemo<App.OfferCancellationPolicy | undefined>(
    () => row.policyType ? { type: row.policyType, policyDetail: [] } : undefined,
    [row.policyType],
  )
  const isFlexible = !!cancellationPolicy && isFlexibleCancellationPolicy(cancellationPolicy)
  const isCashRefund = row.policyType === CANCELLATION_POLICIES.CASH_REFUND_PRIOR_TO_CHECK_IN_TEN_DAYS
  const isRefundable = isFlexible || isCashRefund

  const formattedDeadline = useMemo(() => {
    if (!checkIn) {
      return undefined
    }
    if (isFlexible && cancellationPolicy) {
      const details = getFlexibleCancellationTimelineDetails({ cancellationPolicy, checkInDate: checkIn, propertyTimezoneOffset })
      return details.startBasedOnCheckIn ? formatFlexibleCancellationPolicyDate(details.startBasedOnCheckIn, details.today, intlDateFormatter) : undefined
    }
    if (isCashRefund) {
      const details = getCashRefundCancellationTimelineDetails({ checkInDate: checkIn, propertyTimezoneOffset })
      return details.deadline ? formatFlexibleCancellationPolicyDate(details.deadline, details.today, intlDateFormatter) : undefined
    }
    return undefined
  }, [checkIn, isFlexible, isCashRefund, cancellationPolicy, propertyTimezoneOffset, intlDateFormatter])

  return (
    <StyledRadioInput
      name={radioGroupName}
      value={row.key}
      checked={row.isSelected}
      onChange={onChange}
    >
      <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
        <BodyText
          variant="medium"
          weight={isRefundable ? 'bold' : undefined}
          colour={isRefundable ? titleColour : undefined}
          wrap="no-wrap"
        >
          {copy.title}
        </BodyText>
        {formattedDeadline && (
          <BodyText variant="medium" wrap="no-wrap">
            <FormattedMessage
              id="hv1uXBUiZ3e8KSTtpNec"
              defaultMessage="until {date}"
              description="Date suffix for cancellation policy in the date selection or calendar picker"
              values={{ date: formattedDeadline }}
            />
          </BodyText>
        )}
        <FlashCancellationOptionPriceDifference priceDifference={row.priceDifference} />
        {showReserveForZero && <ReserveForZeroTag />}
      </Group>
    </StyledRadioInput>
  )
}

interface Props {
  rows: Array<FlashCancellationOptionRow>
  reserveForZeroKeys?: ReadonlySet<string>
  checkIn?: string | moment.Moment
  propertyTimezoneOffset?: number
  numberOfDateChangesAllowed?: string
  onChange: (key: string) => void
}

function FlashCancellationOptionSelector(props: Props) {
  const { rows, reserveForZeroKeys, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed, onChange } = props
  const intl = useIntl()
  const radioGroupName = useId()
  const showModal = useContext(ModalContext)

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }, [onChange])

  const showDetails = useCallback(() => {
    showModal(<FlashCancellationOptionsModal
      rows={rows}
      checkIn={checkIn}
      propertyTimezoneOffset={propertyTimezoneOffset}
      numberOfDateChangesAllowed={numberOfDateChangesAllowed}
      onChange={onChange}
    />)
  }, [showModal, rows, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed, onChange])

  const renderHeading = useCallback((chunks: React.ReactNode) => (
    <BodyText key="heading" as="span" variant="medium" weight="bold">{chunks}</BodyText>
  ), [])
  const renderDetailsLink = useCallback((chunks: React.ReactNode) => (
    <TextLink key="details" size="medium" weight="regular" onClick={showDetails}>{chunks}</TextLink>
  ), [showDetails])

  if (rows.length < 2) {
    return null
  }

  return (
    <Group direction="vertical" gap={8}>
      <BodyText variant="medium">
        <FormattedMessage
          id="Yt00OS8ym4OHkykVmEUm"
          defaultMessage="<heading>Cancellation options</heading> · <details>Details</details>"
          description="Heading above the flash hotel package cancellation options, with a link that opens the comparison of every option"
          values={{ heading: renderHeading, details: renderDetailsLink }}
        />
      </BodyText>
      <RadioGroup
        aria-label={intl.formatMessage({
          id: 'J3zjEK2A6_vz3klS2v6N',
          defaultMessage: 'Cancellation options',
          description: 'Accessible label for the radio group that lets the customer choose a cancellation option on a flash hotel package',
        })}
      >
        {rows.map((row) => (
          <FlashCancellationOptionRowInput
            key={row.key}
            row={row}
            radioGroupName={radioGroupName}
            checkIn={checkIn}
            propertyTimezoneOffset={propertyTimezoneOffset}
            showReserveForZero={!!reserveForZeroKeys?.has(row.key)}
            onChange={handleChange}
          />
        ))}
      </RadioGroup>
    </Group>
  )
}

export default FlashCancellationOptionSelector
